import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import "ag-grid-enterprise";
import IntlMessages from "util/IntlMessages";
import {
  Button,
  Input,
  Modal,
  Popover,
  Form,
  Badge,
  Tooltip,
  Card,
  Empty,
  Spin,
} from "antd";
import {
  MailOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  LeftOutlined,
} from "@ant-design/icons";
import AdminNotifications from "components/AdminNotifications";
import { showMessage } from "../../../helpers";
import {
  approveFilingPlansReport,
  getAdminFilingPlansData,
  getFilingPlansData,
  partnerMarkFilingPlansMessageRead,
  postAdminFilingPlansData,
  postFilingPlansData,
  submitFilingPlansReport,
} from "../../../appRedux/actions/FilingPlans";
import moment from "moment";
import { findIndex, isEmpty } from "lodash";
import "./filing-plans.css";
import { getRole } from "../../../helpers";
import { useNavigate, useParams } from "react-router-dom";
import ReportActionButtons from "components/ReportActionButtons";
import LoadingBar from "components/LoadingBar";
const confirm = Modal.confirm;

const FilingPlans = (props) => {
  const [isActionLoading, setIsActionLoading] = useState(false);
  const { id } = useParams(); 
  const navigate = useNavigate();
  const [isDataReady, setIsDataReady] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [isUserAdmin, setIsUserAdmin] = useState(false);
  const [isUserStaff, setIsUserStaff] = useState(false);
  const isUserDiagnosticAdmin = getRole() === "DIAGNOSTIC_ADMIN";
  const isUserDiagnosticReviewer = getRole() === "DIAGNOSTIC_REVIEWER";
  const [messageCount, setMessageCount] = useState(0);
  const [partnerId, setPartnerId] = useState(id || null);
  const [gridApi, setGridApi] = useState({});
  const [rowData, setRowData] = useState(null);
  const [productList, setProductList] = useState([]);
  const [reportDetails, setReportDetails] = useState({});
  const [apiStatusArray, setApiStatusArray] = useState([]);
  const [fdfStatusArray, setFdfStatusArray] = useState([]);
  const [diagnosticsStatusArray, setDiagnosticsStatusArray] = useState([]);
  const [messages, setMessages] = useState([]);
  const [columnDefs, setColumnDefs] = useState([
    {
      headerName: "Sr.No",
      valueGetter: "node.rowIndex + 1",
      width: 100,
    },
    {
      headerName: "Country",
      field: "country",
      editable: false,
    },
  ]);
  const plansList = useSelector(({ filingPlans }) => filingPlans.plansList);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [quarterDropdown, setQuarterDropdown] = useState([]);
  const [quarterDropdownCurrent, setQuarterDropdownCurrent] =
    useState("Loading...");
  const [isHistoricQuarter, setIsHistoricQuarter] = useState(false);
  const [isNotInitial, setIsNotInitial] = useState(false);
  let [flag, setFlag] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setFilingPlans();
    return () => {
      setIsDataReady(false);
    };
  }, []);

  useEffect(() => {
    if (isDataReady) {
      addProduct();
    }
  }, [isDataReady]);
  useEffect(() => {
    if (isNotInitial) {
      addProduct();
    }
  }, [flag]);

  const navigateBack = () => {
    if (isUserAdmin || isUserStaff) {
      navigate("/admin/partner-list");
    }
  };

  const setFilingPlans = (quarter_name = null) => {
    setIsLoading(true);
    if (id) {
      setIsUserAdmin(() => {
        return getRole() === "ADMIN";
      });
      setIsUserStaff(() => {
        return getRole() === "STAFF";
      });
      dispatch(
        getAdminFilingPlansData(
          id,
          (data) => {
            setValues(data);
            setPartnerId(id);
            setIsLoading(false);
          },
          quarter_name,
        ),
      );
    } else {
      dispatch(
        getFilingPlansData((data) => {
          setValues(data);
          setIsLoading(false);
        }, quarter_name),
      );
    }
  };

  const setValues = (data) => {
    // Safely handle potentially undefined or null values
    const filingMeta = data?.filing_meta || {};
    const quarterName = filingMeta?.quarter_name || "";
    
    let rowData = data.rows
      ? data.rows.map((value) => {
          let rowValue = { ...value };
          (data.product_details || []).forEach((value) => {
            if (
              !Object.prototype.hasOwnProperty.call(
                rowValue,
                value.product_name,
              ) ||
              !rowValue[value.product_name]
            ) {
              rowValue[value.product_name] = "0";
            }
          });
          return rowValue;
        })
      : [];
    setRowData(rowData);
    setProductList(data.product_details || []);
    setMessageCount(data.unread_message_count || 0);
    setReportDetails(filingMeta);
    setApiStatusArray(data.status_dropdown?.["API"] || []);
    setFdfStatusArray(data.status_dropdown?.["FDF"] || []);
    setDiagnosticsStatusArray(data.status_dropdown?.["Diagnostics"] || []);
    setMessages(data.messages || []);
    setShowReport(!isEmpty(data));
    
    // Transform quarter dropdown display format - safely handle undefined
    const transformedQuarterDropdown = (data.quarter_dropdown || []).map(quarter => 
      quarter.replace(/-/g, ' to ') // Convert "Jan-Mar 2025" to "Jan to Mar 2025"
    );
    setQuarterDropdown(transformedQuarterDropdown);
    
    setQuarterDropdownCurrent(quarterName.replace(/-/g, ' to '));
    setFlag(++flag);
  };

  const onGridReady = (params) => {
    setGridApi(params.api);
    setIsDataReady(true);
  };

  const addProduct = () => {
    const { report_status } = reportDetails;
    let column;
    if (!isNotInitial) {
      column = [...columnDefs];
    } else {
      column = columnDefs.slice(0, 2);
    }

    productList.forEach((value) => {
      const obj = {
        // Use a dot-free colId + explicit valueGetter/valueSetter instead of
        // `field: value.product_name`. AG Grid treats a dotted `field` string
        // as a nested path (e.g. "P.f" in "Haraka Q Malaria P.f Ag Test" gets
        // parsed as data["Haraka Q Malaria P"]["f Ag Test"]), which silently
        // fails to read/write the real flat key on the row.
        colId: `product_${value.product_id}`,
        headerName: value.product_name,
        valueGetter: (params) => params.data ? params.data[value.product_name] : undefined,
        valueSetter: (params) => {
          params.data[value.product_name] = params.newValue;
          return true;
        },
        editable: isUserDiagnosticReviewer
          ? false
          : isUserAdmin
            ? true
            : isUserStaff
              ? false
              : true, // Partners can always edit
        cellEditorSelector: (params) => {
          // Determine which status array to use based on product type
          let statusArray;
          if (value.product_type === "API") {
            statusArray = apiStatusArray;
          } else if (value.product_type === "Diagnostics") {
            statusArray = diagnosticsStatusArray;
          } else {
            statusArray = fdfStatusArray;
          }
          return {
            component: "agRichSelect",
            params: {
              values: statusArray,
            },
          };
        },
        cellClass: (params) => {
          const { data } = params;
          if (data) {
            return (
              isUserDiagnosticReviewer
                ? false
                : isUserAdmin
                  ? true
                  : isUserStaff
                    ? false
                    : true // Partners can always edit
            )
              ? "editable-cell"
              : "non-editable-cell";
          }
        },
        cellClassRules: {
          "error-cell": (params) => {
            if (value.product_type === "API") {
              return !apiStatusArray.includes(params.value);
            } else if (value.product_type === "Diagnostics") {
              return !diagnosticsStatusArray.includes(params.value);
            } else {
              return !fdfStatusArray.includes(params.value);
            }
          },
        },
        width: 200,
      };
      column.push(obj);
    });
    setColumnDefs(column);
    gridApi.setColumnDefs(column);
    setIsNotInitial(true);
  };

  const checkDataValues = (dataList) => {
    return !dataList.some((value) => checkRow(value));
  };

  const checkRow = (rowData) => {
    let apiFlag = Object.keys(rowData)
      .filter(
        (value) =>
          productList.map((d) => d.product_name).includes(value) &&
          productList[findIndex(productList, { product_name: value })]
            .product_type === "API",
      )
      .some((val) => !apiStatusArray.includes(rowData[val]));

    let fdfFlag = Object.keys(rowData)
      .filter(
        (value) =>
          productList.map((d) => d.product_name).includes(value) &&
          productList[findIndex(productList, { product_name: value })]
            .product_type === "FDF",
      )
      .some((val) => !fdfStatusArray.includes(rowData[val]));

    let diagnosticsFlag = Object.keys(rowData)
      .filter(
        (value) =>
          productList.map((d) => d.product_name).includes(value) &&
          productList[findIndex(productList, { product_name: value })]
            .product_type === "Diagnostics",
      )
      .some((val) => !diagnosticsStatusArray.includes(rowData[val]));

    return apiFlag || fdfFlag || diagnosticsFlag;
  };

  const saveReportBeforeAction = (callback) => {
    setIsActionLoading(true); // Start loading
    gridApi.stopEditing();
    gridApi.selectAll();
    const obj = {};
    let selectedNodes = gridApi.getSelectedNodes().filter((d) => d.data);
    if (checkDataValues(selectedNodes.map((value) => value.data))) {
      selectedNodes.forEach((node) => {
        const { country_id } = node.data;
        if (obj[country_id] === undefined) {
          obj[country_id] = {};
        }
        productList.forEach((value) => {
          obj[country_id][value.product_id] = node.data[value.product_name];
        });
      });
      if (isUserAdmin) {
        if (isHistoricQuarter) {
          // Convert "Oct to Dec 2024" format to "Oct-Dec 2024" format for API
          const apiQuarterFormat = quarterDropdownCurrent.replace(/\s+to\s+/, '-');
          dispatch(
            postAdminFilingPlansData(
              partnerId,
              obj,
              () => {
                setIsActionLoading(false); // Stop loading
                callback();
              },
              apiQuarterFormat,
            ),
          );
        } else {
          dispatch(
            postAdminFilingPlansData(partnerId, obj, () => {
              setIsActionLoading(false); // Stop loading
              callback();
            })
          );
        }
      } else {
        dispatch(
          postFilingPlansData(obj, () => {
            setIsActionLoading(false); // Stop loading
            callback();
          })
        );
      }
      gridApi.deselectAll();
    } else {
      setIsActionLoading(false); // Stop loading on error
      Modal.error({
        title: "Invalid data!",
        content: "Please input valid entries!",
      });
      gridApi.deselectAll();
    }
  };

  const saveData = () => {
    saveReportBeforeAction(() => {
      showMessage("success", "Data recorded successfully!");
    });
  };

  const showSubmitConfirm = () => {
    saveReportBeforeAction(() => {
      confirm({
        content: (
          <Spin spinning={isActionLoading}>
            {showCommentsForm()}
          </Spin>
        ),
        okText: "Submit",
        cancelText: "Cancel",
        title: "Do you want to submit the report?",
        className: "btn-confirm-green",
        onOk(close) {
          form.validateFields().then((values) => {
            let msg = values.commentData ? values.commentData : "";
            setIsActionLoading(true); // Start loading
            dispatch(
              submitFilingPlansReport({ message: msg }, () => {
                setIsActionLoading(false); // Stop loading
                showMessage(
                  "success",
                  "Report has been submitted to the admin!",
                );
                setFilingPlans();
                form.resetFields(["commentData"]);
              }),
            );
            close();
          });
        },
        onCancel() {
          form.resetFields(["commentData"]);
        },
      });
    });
  };

  const approver = (msg, isApproved) => {
    setIsActionLoading(true); // Start loading
    let data = {
      partner_id: partnerId,
      message: msg,
      is_approved: isApproved,
    };
    dispatch(
      approveFilingPlansReport(data, () => {
        setIsActionLoading(false); // Stop loading
        if (isApproved) {
          showMessage("success", "Report Approved!");
        } else {
          showMessage("success", "Report Rejected!");
        }
        form.resetFields(["commentData"]);
        setFilingPlans();
      }),
    );
  };

  const showCommentsForm = () => {
    return (
      <Form form={form} layout="vertical" className="mt-20">
        <div className="mb-10">
          <span>
            {isUserAdmin
              ? "Add comments for partner:"
              : "Add comments for admin:"}
          </span>
        </div>
        <Form.Item
          name="commentData"
          className="mb-0"
          rules={[
            {
              required: isUserAdmin,
              message: isUserAdmin
                ? "Enter comments for partner"
                : "Enter comments for admin",
            },
          ]}
        >
          <Input.TextArea placeholder="Enter comments here" />
        </Form.Item>
      </Form>
    );
  };

  const showRejectConfirm = () => {
    confirm({
      title: "Do you want to reject the report?",
      className: "btn-confirm-red",
      content: (
        <Spin spinning={isActionLoading}>
          {showCommentsForm()}
        </Spin>
      ),
      okText: "Reject",
      cancelText: "Cancel",
      icon: <CloseCircleOutlined />,
      onOk(close) {
        form.validateFields().then((values) => {
          let msg = values.commentData ? values.commentData : "";
          approver(msg, false);
          close();
        });
      },
      onCancel() {
        form.resetFields(["commentData"]);
      },
    });
  };

  const showApproveConfirm = () => {
    confirm({
      title: "Do you want to approve the report?",
      className: "btn-confirm-green",
      content: (
        <Spin spinning={isActionLoading}>
          {showCommentsForm()}
        </Spin>
      ),
      icon: <CheckCircleOutlined />,
      okText: "Approve",
      cancelText: "Cancel",
      onOk(close) {
        form.validateFields().then((values) => {
          let msg = values.commentData ? values.commentData : "";
          approver(msg, true);
          close();
        });
      },
      onCancel() {
        form.resetFields(["commentData"]);
      },
    });
  };

  const downloadExcel = () => {
    gridApi.selectAll();
    gridApi.exportDataAsExcel({
      sheetName: "filing-plans",
      fileName: "filing-plans",
    });
    gridApi.deselectAll();
  };


  // Action handlers for ReportActionButtons
  const handleSave = () => {
    saveData();
  };

  const handleApprove = () => {
    showApproveConfirm();
  };

  const handleReject = () => {
    showRejectConfirm();
  };

  const handleSubmit = () => {
    showSubmitConfirm();
  };

  const handleDownload = () => {
    downloadExcel();
  };

  const handleNotificationClick = () => {
    dispatch(
      partnerMarkFilingPlansMessageRead(
        isUserAdmin ? "admin" : "partner",
        () => setMessageCount(0),
      ),
    );
  };

  const displayHeader = () => {
    if (showReport) {
      if (reportDetails) {
        const {
          partner_name,
          quarter_name,
          report_status,
          approval_time,
          submission_time,
        } = reportDetails;
        return (
          <div className="gx-mb-4">
            <h1 className="title ">
              {isUserAdmin || isUserStaff ? (
                <Tooltip title="Back">
                  <LeftOutlined className="mr-10" onClick={navigateBack} />
                </Tooltip>
              ) : null}
              Filing Plans{" "}
              {isUserAdmin || isUserStaff ? (
                <span className="mr-5">
                  for <span className="text-capitalize">{partner_name}</span>
                </span>
              ) : null}
              <span className="text-capitalize">({quarter_name})</span>
            </h1>
            <h4 className={isUserAdmin || isUserStaff ? "ml-30" : ""}>
              Report Status:
              <span className="text-capitalize"> {" " + report_status}</span>
              <span>
                {!approval_time && !submission_time
                  ? ""
                  : " on " +
                    moment(
                      report_status === "Submitted" ||
                        report_status === "Resubmitted"
                        ? submission_time
                        : approval_time,
                    ).format("Do MMM YYYY, hh:mm A")}
              </span>
            </h4>
          </div>
        );
      }
    } else {
      return (
        <div className="gx-mb-4">
          <h1 className="title ">
            {isUserAdmin ? (
              <LeftOutlined className="mr-5" onClick={navigateBack} />
            ) : null}{" "}
            Filing Plans
            {isUserAdmin && reportDetails ? (
              <span>
                for{" "}
                <span className="text-capitalize">
                  {reportDetails.partner_name}
                </span>
              </span>
            ) : null}
          </h1>
        </div>
      );
    }
  };

  const displayCTA = () => {
    if (showReport && reportDetails) {
      const { report_status } = reportDetails;

      if (isUserAdmin || isUserStaff || isUserDiagnosticAdmin || isUserDiagnosticReviewer) {
        // Admin-style view: Save, Approve, Reject, Download, Mail, Quarter Dropdown
        return (
          <ReportActionButtons
            // Quarter dropdown props
            quarterDropdownCurrent={quarterDropdownCurrent}
            onQuarterChange={changeQuarter}
            quarterDropdown={quarterDropdown}

            // Action button props
            onSave={handleSave}
            onApprove={handleApprove}
            onReject={handleReject}
            onDownload={handleDownload}

            // Permission props
            isUserAdmin={isUserAdmin || isUserDiagnosticAdmin}
            isUserStaff={isUserStaff}
            isHistoricQuarter={isHistoricQuarter}

            // Report status
            reportStatus={report_status}

            // Notification props
            messageCount={messageCount}
            messages={messages}
            onNotificationClick={handleNotificationClick}

            // Disable states
            disableSave={isUserStaff || isUserDiagnosticReviewer}
            disableApprove={isHistoricQuarter || isUserStaff || isUserDiagnosticReviewer}
            disableReject={isHistoricQuarter || isUserStaff || isUserDiagnosticReviewer}

            // Show/hide specific buttons - Admin/Staff configuration
            showSave={true}
            showApprove={true}
            showReject={true}
            showSubmit={false}  // No submit for admin/staff
            showDownload={true}
            showQuarterDropdown={quarterDropdown && quarterDropdown.length > 0}

            // Skip confirmation props
            skipApproveConfirm={true}
            skipRejectConfirm={true}

            // Custom styles
            className="filing-plans-actions"
          />
        );
      } else {
        // Partner view: Save, Submit Report, Mail, Download, Quarter Dropdown
        return (
          <ReportActionButtons
            // Quarter dropdown props
            quarterDropdownCurrent={quarterDropdownCurrent}
            onQuarterChange={changeQuarter}
            quarterDropdown={quarterDropdown}

            // Action button props
            onSave={handleSave}
            onSubmit={handleSubmit}
            onDownload={handleDownload}

            // Permission props
            isUserAdmin={isUserAdmin}
            isUserStaff={isUserStaff}
            isHistoricQuarter={isHistoricQuarter}

            // Report status
            reportStatus={report_status}

            // Notification props
            messageCount={messageCount}
            messages={messages}
            onNotificationClick={handleNotificationClick}

            // Disable states
            disableSave={report_status === "Approved" || isHistoricQuarter || isUserDiagnosticReviewer}
            disableSubmit={report_status === "Approved" || isHistoricQuarter || isUserDiagnosticReviewer}

            // Show/hide specific buttons - Partner configuration
            showSave={true}
            showApprove={false}  // No approve for partners
            showReject={false}   // No reject for partners
            showSubmit={true}
            showDownload={true}
            showQuarterDropdown={quarterDropdown && quarterDropdown.length > 0}
            skipSubmitConfirm={true}

            // Custom styles
            className="filing-plans-actions"
          />
        );
      }
    } else {
      return null;
    }
  };

  const handleCellValueChange = (params) => {
    const { colDef, node, newValue, oldValue } = params;
    let rowNode = gridApi.getRowNode(node.id);
    if (newValue !== oldValue && newValue === "") {
      // Product columns use colId (not field) since their field would
      // otherwise be dot-parsed by AG Grid — see addProduct().
      rowNode.setDataValue(colDef.colId || colDef.field, "0");
    }
  };

  const setReportHeight = () => {
    return window.innerHeight - 120;
  };

  const changeQuarter = (e) => {
    // Handle both event object (from select) and direct value (from ReportActionButtons)
    const quarterValue = e.target ? e.target.value : e;
    
    // Convert "Jan to Mar 2025" format to "Jan-Mar 2025" format for API
    const apiQuarterFormat = quarterValue.replace(/\s+to\s+/, '-');

    setColumnDefs(columnDefs.slice(0, 2));
    if (quarterValue !== quarterDropdown[0]) {
      setFilingPlans(apiQuarterFormat);
      setIsHistoricQuarter(true);
    } else {
      setFilingPlans();
      setIsHistoricQuarter(false);
    }
  };

  return (
    <div>
      {isLoading ? (
        <div>
          <LoadingBar />
        </div>
      ) : (
        <>
          <Spin spinning={isActionLoading} tip="Processing...">
            <div className="gx-flex-row gx-justify-content-between">
              {displayHeader()}
              {displayCTA()}
            </div>
            {showReport ? (
              <div
                className="ag-theme-balham ag-scroll-bar-fix"
                style={{ height: setReportHeight(), paddingBottom: 20 }}
              >
                <AgGridReact
                  onGridReady={onGridReady}
                  columnDefs={columnDefs}
                  rowData={rowData}
                  defaultColGroupDef={{ marryChildren: true }}
                  defaultColDef={{
                    filter: true,
                    sortable: true,
                    width: 300,
                    resizable: true,
                  }}
                  enableRangeSelection={true}
                  enableCellChangeFlash={true}
                  onCellValueChanged={handleCellValueChange}
                  animateRows={true}
                />
              </div>
            ) : (
              <Card>
                <Empty
                  description={
                    <p className="empty-msg">
                      <span>No Data Available</span>
                      <br />
                      <span>Please contact Admin for more information!</span>
                    </p>
                  }
                />
              </Card>
            )}
          </Spin>
        </>
      )}
    </div>
  );
};

export default FilingPlans;